import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Users2,
  Building2,
  Clock,
  Landmark,
  Loader2,
  ArrowRight,
} from "lucide-react";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed, num } from "@/lib/format";
import { printFinancialReport } from "@/lib/financialReportPrint";
import { cn } from "@/lib/utils";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";

const TABS = [
  { id: "statements", label: "Financial Statements", path: "/reports/financial-statements", icon: Landmark },
  { id: "customer-age", label: "Customer Age Wise", path: "/reports/customer-age-wise", icon: Clock },
  { id: "supplier-age", label: "Supplier Age Wise", path: "/reports/supplier-age-wise", icon: Clock },
  { id: "customer-ledger", label: "Customer Ledger", path: "/reports/customer-ledger", icon: Users2 },
  { id: "supplier-ledger", label: "Supplier Ledger", path: "/reports/supplier-ledger", icon: Building2 },
];

// Accounts module hidden from nav — statement shortcuts commented out
// const STATEMENT_LINKS = [
//   { label: "Account Ledger", path: "/accounts/ledger", icon: FileText, desc: "Account-wise voucher entries" },
//   { label: "Trial Balance", path: "/accounts/trial-balance", icon: Scale, desc: "Debit/credit summary by account" },
//   { label: "Profit & Loss", path: "/accounts/profit-loss", icon: TrendingUp, desc: "Income and expense for period" },
//   { label: "Balance Sheet", path: "/accounts/balance-sheet", icon: Landmark, desc: "Assets and liabilities position" },
// ];
const STATEMENT_LINKS = [];

const ENDPOINTS = {
  "customer-age": "/reports/financial/customer-age-wise",
  "supplier-age": "/reports/financial/supplier-age-wise",
  "customer-ledger": "/reports/financial/customer-ledger",
  "supplier-ledger": "/reports/financial/supplier-ledger",
};

const defaultFromDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function FinancialReports() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();

  const activeTab = useMemo(() => {
    if (location.pathname.includes("supplier-age")) return "supplier-age";
    if (location.pathname.includes("customer-age")) return "customer-age";
    if (location.pathname.includes("supplier-ledger")) return "supplier-ledger";
    if (location.pathname.includes("customer-ledger")) return "customer-ledger";
    return "statements";
  }, [location.pathname]);

  const isAgeWise = activeTab === "customer-age" || activeTab === "supplier-age";
  const isLedger = activeTab === "customer-ledger" || activeTab === "supplier-ledger";
  const isCustomer = activeTab.startsWith("customer");

  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(today);
  const [asOfDate, setAsOfDate] = useState(today);
  const [partyId, setPartyId] = useState("");

  const partyEndpoint = isCustomer ? "/customers" : "/suppliers";

  const { data: partiesData } = useQuery({
    queryKey: [partyEndpoint, branchId],
    queryFn: async () => {
      const { data: res } = await api.get(partyEndpoint, { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId && isLedger),
  });

  const parties = partiesData?.customers ?? partiesData?.suppliers ?? [];

  useEffect(() => {
    if (isLedger && !partyId && parties.length) {
      setPartyId(String(parties[0].id));
    }
  }, [parties, partyId, isLedger]);

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["financial-report", activeTab, branchId, fromDate, toDate, asOfDate, partyId],
    queryFn: async () => {
      if (activeTab === "statements") return null;
      if (isAgeWise) {
        const { data: res } = await api.get(ENDPOINTS[activeTab], {
          params: { ...reportParams, as_of_date: asOfDate },
        });
        return res;
      }
      const { data: res } = await api.get(ENDPOINTS[activeTab], {
        params: {
          ...reportParams,
          party_id: partyId,
          from_date: fromDate,
          to_date: toDate,
        },
      });
      return res;
    },
    enabled: Boolean(
      branchId &&
        activeTab !== "statements" &&
        (isAgeWise ? asOfDate : partyId && fromDate && toDate),
    ),
  });

  const title = TABS.find((t) => t.id === activeTab)?.label ?? "Financial Reports";

  return (
    <div className="min-w-0">
      <PageHeader
        title={title}
        subtitle={
          activeTab === "statements"
            ? "Ledger, trial balance, P&L and balance sheet"
            : isAgeWise
              ? "Outstanding balances by ageing bucket"
              : "Party-wise debit/credit ledger"
        }
        actions={
          <ReportExportButtons
            disabled={activeTab === "statements" || !report}
            title={title}
            filename={`financial-${activeTab}`}
            onPrint={
              activeTab === "statements"
                ? undefined
                : () => printFinancialReport(report, activeTab, { branchId, allBranches })
            }
          />
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.path)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                activeTab === tab.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-600 hover:text-slate-900",
              )}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "statements" && <StatementsHub navigate={navigate} />}

      {activeTab !== "statements" && (
        <Card className="p-5 mb-4">
          <SectionTitle className="!mb-3">Filters</SectionTitle>
          <div className="flex flex-wrap items-end gap-4">
            {isAgeWise ? (
              <div>
                <Label className="text-[12px] text-slate-600 mb-1 block">As of date</Label>
                <Input type="date" className="h-9 w-[160px]" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-[12px] text-slate-600 mb-1 block">Party</Label>
                  <Select value={partyId} onValueChange={setPartyId}>
                    <SelectTrigger className="h-9 w-[240px]"><SelectValue placeholder="Select party" /></SelectTrigger>
                    <SelectContent>
                      {parties.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.code} — {p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[12px] text-slate-600 mb-1 block">From</Label>
                  <Input type="date" className="h-9 w-[160px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>
                <div>
                  <Label className="text-[12px] text-slate-600 mb-1 block">To</Label>
                  <Input type="date" className="h-9 w-[160px]" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
              </>
            )}
            {(isLoading || isFetching) && (
              <Pill className="mb-1"><Loader2 size={12} className="animate-spin mr-1" /> Loading…</Pill>
            )}
          </div>
        </Card>
      )}

      {report && isAgeWise && <AgeWiseView report={report} />}
      {report && isLedger && <PartyLedgerView report={report} />}
    </div>
  );
}

function StatementsHub({ navigate }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {STATEMENT_LINKS.map((link) => {
        const Icon = link.icon;
        return (
          <Card
            key={link.path}
            className="p-5 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
            onClick={() => navigate(link.path)}
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-700">
                <Icon size={18} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-900">{link.label}</p>
                <p className="text-[12.5px] text-slate-500 mt-0.5">{link.desc}</p>
              </div>
              <ArrowRight size={16} className="text-slate-400 mt-1" />
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function AgeWiseView({ report }) {
  const s = report.summary || {};

  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "Parties", value: num(s.party_count) },
          { label: "Total outstanding", value: aed(s.total_outstanding), emphasize: true },
          { label: "0–30 days", value: aed(s.current) },
          { label: "31–60 days", value: aed(s.days_31_60) },
          { label: "61–90 days", value: aed(s.days_61_90) },
          { label: "90+ days", value: aed(s.days_90_plus) },
        ]}
      />

      <ReportTableCard title="Ageing detail">
        <table>
          <thead>
            <tr>
              <th className="text-left">Code</th>
              <th className="text-left">Party</th>
              <th className="text-right">Limit</th>
              <th className="text-right">Total</th>
              <th className="text-right">0–30</th>
              <th className="text-right">31–60</th>
              <th className="text-right">61–90</th>
              <th className="text-right">90+</th>
            </tr>
          </thead>
          <tbody>
            {(report.rows || []).map((r) => (
              <tr key={r.party_id} className={cn(r.over_limit && "bg-red-50/50")}>
                <td className="font-mono text-[12px]">{r.party_code}</td>
                <td>{r.party_name}</td>
                <td className="text-right font-mono">{aed(r.credit_limit)}</td>
                <td className="text-right font-mono font-medium">{aed(r.total_outstanding)}</td>
                <td className="text-right font-mono">{aed(r.current)}</td>
                <td className="text-right font-mono">{aed(r.days_31_60)}</td>
                <td className="text-right font-mono">{aed(r.days_61_90)}</td>
                <td className="text-right font-mono">{aed(r.days_90_plus)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}

function PartyLedgerView({ report }) {
  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "Opening", value: aed(report.opening_balance) },
          { label: "Total debit", value: aed(report.total_debit) },
          { label: "Total credit", value: aed(report.total_credit) },
          { label: "Closing", value: aed(report.closing_balance), emphasize: true },
        ]}
      />

      <ReportTableCard title={`${report.party?.code} — ${report.party?.name}`}>
        <table>
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Reference</th>
              <th className="text-left">Type</th>
              <th className="text-left">Narration</th>
              <th className="text-right">Debit</th>
              <th className="text-right">Credit</th>
              <th className="text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-slate-50/50">
              <td colSpan={4} className="italic text-slate-600">Opening balance</td>
              <td />
              <td />
              <td className="text-right font-mono font-medium">{aed(report.opening_balance)}</td>
            </tr>
            {(report.entries || []).map((e, i) => (
              <tr key={`${e.reference}-${i}`}>
                <td>{e.date}</td>
                <td className="font-mono text-[12px]">{e.reference}</td>
                <td>{e.type}</td>
                <td className="text-slate-600">{e.narration || "—"}</td>
                <td className="text-right font-mono">{e.debit ? aed(e.debit) : ""}</td>
                <td className="text-right font-mono">{e.credit ? aed(e.credit) : ""}</td>
                <td className="text-right font-mono font-medium">{aed(e.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}
